import React, { useEffect, PropsWithChildren } from 'react';
import { LocalizationProvider } from '@im/react-localization/localization/LocalizationProvider';
import { useLocalizationContext } from '@im/react-localization';

import {
  useBuildVars,
  LOCALE_SUPPORTED,
  LOCALE_DEFAULT,
  sessionId,
  I18N_API_HEADERS as apiHeaders,
} from 'src/config';

type LocalizationProviderConnectorProps = PropsWithChildren<{
  apiHeaders?: Record<string, string>;
  preloadResources?: string[];
  supportedLocales?: string[];
  useApi?: boolean;
}>;

export default function LocalizationProviderConnector({
  apiHeaders = {},
  children,
  preloadResources = [LOCALE_DEFAULT.toLowerCase()],
  supportedLocales = LOCALE_SUPPORTED,
  useApi = false,
}: LocalizationProviderConnectorProps) {
  const buildVars = useBuildVars();

  const isNonProd = buildVars.buildEnvironment !== 'production';

  const url = useApi
    ? `${buildVars.TRANSLATION_API_BASE}/shared/v1/translations`
    : '/translations';

  return (
    <LocalizationProvider
      apiHeaders={{ 'IM-DDSessionId': sessionId, ...apiHeaders }}
      appId={'gds' as any}
      defaultLocale={LOCALE_DEFAULT}
      preloadResources={
        buildVars.buildEnvironment !== 'test' ? preloadResources : undefined
      }
      showLogs={isNonProd}
      supportedLocales={supportedLocales}
      url={url}
      useApi={useApi}
    >
      {children}
    </LocalizationProvider>
  );
}

function SetLocale({
  children,
  locale: outerLocale,
}: PropsWithChildren<{
  locale: string;
}>) {
  const { locale: innerLocale, setLocale } = useLocalizationContext();

  useEffect(() => {
    outerLocale !== innerLocale && setLocale(outerLocale);
  }, [outerLocale, innerLocale, setLocale]);

  return <>{children}</>;
}

export function NestedApiLocalizationProvider({
  children,
  ...props
}: LocalizationProviderConnectorProps) {
  const { buildEnvironment } = useBuildVars();
  const { locale } = useLocalizationContext();
  const useApi =
    buildEnvironment !== 'development' || locale !== LOCALE_DEFAULT;
  const localizationProps = useApi && {
    ...props,
    apiHeaders,
    useApi,
  };

  return (
    <LocalizationProviderConnector {...localizationProps}>
      <SetLocale locale={locale}>{children}</SetLocale>
    </LocalizationProviderConnector>
  );
}
